import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Stethoscope, Baby, Pill, HeartPulse, Activity, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gynecology() {
  const { t } = useLanguage();

  useEffect(() => {
    axios.post(`${API}/analytics/visit`, { page: '/gynecology' }).catch(() => {});
  }, []);

  const services = [
    { icon: Stethoscope, title: 'Annual Exam & Pap Smear', desc: 'Routine pelvic exam, cervical screening, breast check — all in one visit. Most women are in and out in under 30 minutes.' },
    { icon: Baby, title: 'Birth Control That Fits You', desc: 'We fit IUDs in-clinic, prescribe pills, patches and rings, and walk you through pros and cons honestly. Switching is easy if the first choice does not work.' },
    { icon: Pill, title: 'Period Problems & Hormones', desc: 'Heavy or irregular cycles, painful periods, PCOS, fibroids, suspected endometriosis — we run the workup and start treatment, not just hand you a referral slip.' },
    { icon: HeartPulse, title: 'Early Pregnancy Care', desc: 'Pregnancy confirmation, dating ultrasounds, and prenatal monitoring through the first trimester before handover to your OB if you want one.' },
    { icon: Activity, title: 'Menopause Management', desc: 'For hot flashes, sleep loss, mood changes, and intimate dryness — we discuss HRT, non-hormonal options, and lifestyle adjustments without pushing one answer.' },
    { icon: ShieldCheck, title: 'STI Screening (No Awkwardness)', desc: 'Full panel testing including chlamydia, gonorrhea, HPV, syphilis and HIV. Results back in 2 to 5 days, treatment same-day if positive.' },
  ];

  return (
    <div data-testid="gynecology-page">
      <section className="py-24 sm:py-32 bg-secondary/50">
        <div className="container mx-auto px-6 md:px-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('gynecology.title')}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light">{t('gynecology.title')}</h1>
          <p className="text-base text-muted-foreground mt-4 max-w-2xl mx-auto">{t('gynecology.subtitle')}</p>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <Card key={i} className="transition-card rounded-2xl border-border" data-testid={`gynecology-service-${i}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                    <s.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-medium">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">Need to See a Gynecologist?</h2>
          <p className="text-lg opacity-80 mt-4 max-w-lg mx-auto">Most appointments can be booked within the same week. Call or message and we will fit you in.</p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="mt-8 rounded-full px-10" data-testid="gynecology-cta">{t('hero.cta')} <ArrowRight className="ml-2 w-4 h-4" /></Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
